import { I18n } from '@aws-amplify/core';

export type Question = {
	key: string;
	text: string;
	required: boolean;
	depends_on?: string[];
	conditional_required?: boolean;
};

export type Category = {
	key: string;
	name: string;
	mandatory_questions: Question[];
	conditional_questions: Question[];
};

export const RISK_FACTOR_CATEGORIES: Category[] = [
	{
		key: 'lift_and_lower',
		name: I18n.get('Lifting and lowering'),
		mandatory_questions: [
			{
				key: 'lift_lower_loads_2_25kg',
				text: 'Lifting and/or lowering loads weighing more than 2 kg and up to 25 kg?',
				required: true
			},
			{
				key: 'lift_lower_frequency_1_360_per_hour',
				text: 'Does the worker perform lifting/lowering operations daily and cyclically with a frequency of ≥ 1 per hour or ≤ 360 per hour? (If done sporadically, indicate "No")',
				required: true
			},
			{
				key: 'lift_lower_loads_over_25kg',
				text: 'Lifting and/or lowering loads weighing more than 25 kg?',
				required: true
			}
		],
		conditional_questions: [
			{
				key: 'lift_above_shoulder_30cm',
				text: 'Does the worker lift, hold, and deposit the load exceeding 30 cm above shoulder height with their hands?',
				required: false,
				depends_on: [
					'lift_lower_loads_2_25kg',
					'lift_lower_frequency_1_360_per_hour',
					'lift_lower_loads_over_25kg'
				],
				conditional_required: true
			},
			{
				key: 'lift_horizontally_80cm_from_ankles',
				text: 'Does the worker lift, hold, and deposit the load more than 80 cm horizontally from the midpoint between the ankles?',
				required: false,
				depends_on: [
					'lift_lower_loads_2_25kg',
					'lift_lower_frequency_1_360_per_hour',
					'lift_lower_loads_over_25kg'
				],
				conditional_required: true
			},
			{
				key: 'twist_bend_waist_30_degrees',
				text: 'Between lifting and depositing the load, does the worker twist or bend their waist more than 30º to either side (or both) from the sagittal plane?',
				required: false,
				depends_on: [
					'lift_lower_loads_2_25kg',
					'lift_lower_frequency_1_360_per_hour',
					'lift_lower_loads_over_25kg'
				],
				conditional_required: true
			},
			{
				key: 'irregular_difficult_to_grasp_loads',
				text: 'Do the loads have irregular shapes, are difficult to grasp, deform, or have movement inside?',
				required: false,
				depends_on: [
					'lift_lower_loads_2_25kg',
					'lift_lower_frequency_1_360_per_hour',
					'lift_lower_loads_over_25kg'
				],
				conditional_required: true
			},
			{
				key: 'lift_with_one_arm',
				text: 'Does the worker lift, hold, and deposit the load with one arm?',
				required: false,
				depends_on: [
					'lift_lower_loads_2_25kg',
					'lift_lower_frequency_1_360_per_hour',
					'lift_lower_loads_over_25kg'
				],
				conditional_required: true
			},
			{
				key: 'early_signs_diseases_article_1',
				text: 'Does the worker show any early signs of the diseases mentioned in Article 1 of this Resolution?',
				required: false,
				depends_on: [
					'lift_lower_loads_2_25kg',
					'lift_lower_frequency_1_360_per_hour',
					'lift_lower_loads_over_25kg'
				],
				conditional_required: true
			}
		]
	},
	{
		key: 'push_pull',
		name: I18n.get('Manual load push and pull'),
		mandatory_questions: [
			{
				key: 'push_pull_cyclical_tasks_daily',
				text: 'Are cyclical tasks performed daily, with a frequency of ≥ 1 movement per day? (If sporadic, indicate "No")',
				required: true
			},
			{
				key: 'push_pull_distance_over_60m',
				text: 'Does the worker move by pushing and/or pulling an object over a distance greater than 60 meters?',
				required: true
			},
			{
				key: 'push_pull_force_over_34kg',
				text: 'Are objects (bags, boxes, furniture, machines, etc.) pushed or pulled cyclically with a force measured with a dynamometer exceeding 34 kg?',
				required: true
			}
		],
		conditional_questions: [
			{
				key: 'push_initial_effort_12kg_men_10kg_women',
				text: 'Is an initial effort required to push the rolling object measured with a dynamometer >= 12 kg for men or 10 kg for women?',
				required: false,
				depends_on: [
					'push_pull_cyclical_tasks_daily',
					'push_pull_distance_over_60m',
					'push_pull_force_over_34kg'
				],
				conditional_required: true
			},
			{
				key: 'pull_initial_effort_10kg',
				text: 'Is an initial effort required to pull the rolling object measured with a dynamometer >= 10 kg for men or women?',
				required: false,
				depends_on: [
					'push_pull_cyclical_tasks_daily',
					'push_pull_distance_over_60m',
					'push_pull_force_over_34kg'
				],
				conditional_required: true
			},
			{
				key: 'rolling_object_difficulty_uneven_surface',
				text: 'Is the rolling object pushed and/or pulled with difficulty (the sliding surface is uneven, there are ramps to go up or down, there are breaks or obstacles in the path, wheels in poor condition, poorly designed handle, etc.)?',
				required: false,
				depends_on: [
					'push_pull_cyclical_tasks_daily',
					'push_pull_distance_over_60m',
					'push_pull_force_over_34kg'
				],
				conditional_required: true
			},
			{
				key: 'rolling_object_one_hand_uncomfortable_height',
				text: 'Can the rolling object not be pushed and/or pulled with both hands, and if allowed, is the hand support at an uncomfortable height (above the chest or below the waist)?',
				required: false,
				depends_on: [
					'push_pull_cyclical_tasks_daily',
					'push_pull_distance_over_60m',
					'push_pull_force_over_34kg'
				],
				conditional_required: true
			},
			{
				key: 'rolling_object_maintained_effort_motion',
				text: 'In the act of pushing and/or pulling, is the initial effort required significantly maintained once the object is in motion (is there wheel jamming, jerking, or lack of uniform sliding)?',
				required: false,
				depends_on: [
					'push_pull_cyclical_tasks_daily',
					'push_pull_distance_over_60m',
					'push_pull_force_over_34kg'
				],
				conditional_required: true
			},
			{
				key: 'rolling_object_one_hand_only',
				text: 'Does the worker push or pull the rolling object using only one hand?',
				required: false,
				depends_on: [
					'push_pull_cyclical_tasks_daily',
					'push_pull_distance_over_60m',
					'push_pull_force_over_34kg'
				],
				conditional_required: true
			},
			{
				key: 'push_pull_early_signs_diseases_article_1',
				text: 'Does the worker show any early signs of the diseases mentioned in Article 1 of this Resolution?',
				required: false,
				depends_on: [
					'push_pull_cyclical_tasks_daily',
					'push_pull_distance_over_60m',
					'push_pull_force_over_34kg'
				],
				conditional_required: true
			}
		]
	},
	{
		key: 'transport',
		name: I18n.get('Manual load transport'),
		mandatory_questions: [
			{
				key: 'transport_loads_2_25kg',
				text: 'Manually transporting loads weighing more than 2 kg and up to 25 kg?',
				required: true
			},
			{
				key: 'transport_holding_load_over_1m',
				text: 'Does the worker move while manually holding the load over a distance greater than 1 meter?',
				required: true
			},
			{
				key: 'transport_cyclical_daily',
				text: 'Is it performed daily in a cyclical manner? (if sporadic, indicate "No")',
				required: true
			},
			{
				key: 'transport_distance_over_20m',
				text: 'Is manual transport of loads over a distance greater than 20 meters?',
				required: true
			},
			{
				key: 'transport_loads_over_25kg',
				text: 'Is manual transport of loads weighing more than 25 kg?',
				required: true
			}
		],
		conditional_questions: [
			{
				key: 'transport_1_10m_cumulative_mass_10000kg',
				text: 'Under normal lifting conditions, does the worker transport the load between 1 and 10 meters with a cumulative mass (the product of mass by frequency) greater than 10,000 kg during the usual workday?',
				required: false,
				depends_on: [
					'transport_loads_2_25kg',
					'transport_move_load_over_1m',
					'transport_cyclical_daily',
					'transport_distance_over_20m',
					'transport_loads_over_25kg'
				],
				conditional_required: true
			},
			{
				key: 'transport_10_20m_cumulative_mass_6000kg',
				text: 'Under normal lifting conditions, does the worker transport the load between 10 and 20 meters with a cumulative mass (the product of mass by frequency) greater than 6,000 kg during the usual workday?',
				required: false,
				depends_on: [
					'transport_loads_2_25kg',
					'transport_move_load_over_1m',
					'transport_cyclical_daily',
					'transport_distance_over_20m',
					'transport_loads_over_25kg'
				],
				conditional_required: true
			},
			{
				key: 'transport_irregular_difficult_loads',
				text: 'Do the loads have irregular shapes, are difficult to grasp, deform, or have movement inside?',
				required: false,
				depends_on: [
					'transport_loads_2_25kg',
					'transport_move_load_over_1m',
					'transport_cyclical_daily',
					'transport_distance_over_20m',
					'transport_loads_over_25kg'
				],
				conditional_required: true
			},
			{
				key: 'transport_early_signs_diseases_article_1',
				text: 'Does the worker show any early signs of the diseases mentioned in Article 1 of this Resolution?',
				required: false,
				depends_on: [
					'transport_loads_2_25kg',
					'transport_move_load_over_1m',
					'transport_cyclical_daily',
					'transport_distance_over_20m',
					'transport_loads_over_25kg'
				],
				conditional_required: true
			}
		]
	},
	{
		key: 'standing',
		name: I18n.get('Standing position'),
		mandatory_questions: [
			{
				key: 'standing_2_consecutive_hours_no_sitting',
				text: 'Does the job require standing for 2 consecutive hours or more without the possibility of sitting?',
				required: true
			}
		],
		conditional_questions: [
			{
				key: 'standing_3_hours_limited_mobility_100m_per_hour',
				text: 'Does the job involve standing for 3 consecutive hours or more without the possibility of sitting and with limited mobility (walking no more than 100 meters/hour)?',
				required: false,
				depends_on: ['standing_2_consecutive_hours_no_sitting'],
				conditional_required: true
			},
			{
				key: 'standing_2_hours_lifting_transporting_over_2kg',
				text: 'Does the job involve standing for 2 consecutive hours or more without the possibility of sitting or moving, or with limited mobility, lifting and/or transporting loads > 2 kg?',
				required: false,
				depends_on: ['standing_2_consecutive_hours_no_sitting'],
				conditional_required: true
			},
			{
				key: 'standing_prolonged_extreme_temperature_humidity',
				text: 'Are tasks performed with prolonged standing in environments where temperature and humidity exceed legally permissible limits and require physical activity?',
				required: false,
				depends_on: ['standing_2_consecutive_hours_no_sitting'],
				conditional_required: true
			},
			{
				key: 'standing_early_signs_diseases_article_1',
				text: 'Does the worker show any early signs of the diseases mentioned in Article 1 of this Resolution?',
				required: false,
				depends_on: ['standing_2_consecutive_hours_no_sitting'],
				conditional_required: true
			}
		]
	},
	{
		key: 'repetitive_movements',
		name: I18n.get('Repetitive movements of upper limbs'),
		mandatory_questions: [
			{
				key: 'repetitive_movements_daily_4_hours_cyclical',
				text: 'Performing daily, one or more tasks using the upper limbs for 4 or more hours during the regular workday in a cyclical manner (continuously or alternately)?',
				required: true
			}
		],
		conditional_questions: [
			{
				key: 'repetitive_upper_limbs_active_40_percent_cycle',
				text: 'Are the upper limbs active for more than 40% of the total work cycle time?',
				required: false,
				depends_on: ['repetitive_movements_daily_4_hours_cyclical'],
				conditional_required: true
			},
			{
				key: 'repetitive_effort_borg_3_over_6_seconds_once_per_minute',
				text: 'Is there an effort greater than moderate at 3 on the Borg Scale, for more than 6 seconds and more than once per minute?',
				required: false,
				depends_on: ['repetitive_movements_daily_4_hours_cyclical'],
				conditional_required: true
			},
			{
				key: 'repetitive_effort_borg_over_7',
				text: 'Is there an effort greater than 7 on the Borg scale?',
				required: false,
				depends_on: ['repetitive_movements_daily_4_hours_cyclical'],
				conditional_required: true
			},
			{
				key: 'repetitive_early_manifestations_diseases_article_1',
				text: 'Does the worker show any early manifestations of the diseases mentioned in Article 1 of this Resolution?',
				required: false,
				depends_on: ['repetitive_movements_daily_4_hours_cyclical'],
				conditional_required: true
			}
		]
	},
	{
		key: 'forced_posture',
		name: I18n.get('Forced posture'),
		mandatory_questions: [
			{
				key: 'forced_posture_regularly_workday',
				text: 'Adopting forced postures regularly during the workday, with or without applying force. (Occasional postures should not be considered)?',
				required: true
			}
		],
		conditional_questions: [
			{
				key: 'forced_posture_neck_extension_flexion_lateralization_rotation',
				text: 'Is the neck in extension, flexion, lateralization, and/or rotation?',
				required: false,
				depends_on: ['forced_posture_regularly_workday'],
				conditional_required: true
			},
			{
				key: 'forced_posture_arms_above_shoulder_supination_pronation_rotation',
				text: 'Are the arms above shoulder level or with movements of supination, pronation, or rotation?',
				required: false,
				depends_on: ['forced_posture_regularly_workday'],
				conditional_required: true
			},
			{
				key: 'forced_posture_wrists_hands_flexion_extension_ulnar_radial_deviation',
				text: 'Are the wrists and hands in flexion, extension, ulnar or radial deviation?',
				required: false,
				depends_on: ['forced_posture_regularly_workday'],
				conditional_required: true
			},
			{
				key: 'forced_posture_waist_flexion_extension_lateralization_rotation',
				text: 'Is the waist in flexion, extension, lateralization, and/or rotation?',
				required: false,
				depends_on: ['forced_posture_regularly_workday'],
				conditional_required: true
			},
			{
				key: 'forced_posture_lower_limbs_kneeling_squatting',
				text: 'Are the lower limbs working in a kneeling or squatting position?',
				required: false,
				depends_on: ['forced_posture_regularly_workday'],
				conditional_required: true
			},
			{
				key: 'forced_posture_early_signs_diseases_article_1',
				text: 'Does the worker show any early signs of the diseases mentioned in Article 1 of this Resolution?',
				required: false,
				depends_on: ['forced_posture_regularly_workday'],
				conditional_required: true
			}
		]
	},
	{
		key: 'hand_arm_vibration',
		name: I18n.get('Hand-arm vibration'),
		mandatory_questions: [
			{
				key: 'hand_arm_vibration_tools_produce_vibrations',
				text: 'Working with tools that produce vibrations (jackhammer, drill, screwdrivers, polishers, grinders, others)?',
				required: true
			},
			{
				key: 'hand_arm_vibration_holding_pieces_while_machining',
				text: 'Holding pieces with hands while they are being machined?',
				required: true
			},
			{
				key: 'hand_arm_vibration_holding_levers_steering_wheels',
				text: 'Holding levers, steering wheels, etc. that transmit vibrations?',
				required: true
			}
		],
		conditional_questions: [
			{
				key: 'hand_arm_vibration_exceeds_table_1_annex_v_resolution_295_03',
				text: 'Does the vibration value exceed the limits established in Table I, in the section corresponding to Hand-Arm Vibration, Annex V, Resolution MTEySS N° 295/03?',
				required: false,
				depends_on: [
					'hand_arm_vibration_tools_produce_vibrations',
					'hand_arm_vibration_holding_pieces_while_machining',
					'hand_arm_vibration_holding_levers_steering_wheels'
				],
				conditional_required: true
			},
			{
				key: 'hand_arm_vibration_early_signs_diseases_article_1',
				text: 'Does the worker show any early signs of the diseases mentioned in Article 1 of this Resolution?',
				required: false,
				depends_on: [
					'hand_arm_vibration_tools_produce_vibrations',
					'hand_arm_vibration_holding_pieces_while_machining',
					'hand_arm_vibration_holding_levers_steering_wheels'
				],
				conditional_required: true
			}
		]
	},
	{
		key: 'whole_body_vibration',
		name: I18n.get('Whole body vibration'),
		mandatory_questions: [
			{
				key: 'whole_body_vibration_tools_produce_vibrations',
				text: 'Working with tools that produce vibrations (jackhammer, drill, screwdrivers, polishers, grinders, others)?',
				required: true
			},
			{
				key: 'whole_body_vibration_holding_pieces_while_machining',
				text: 'Holding pieces with hands while they are being machined?',
				required: true
			},
			{
				key: 'whole_body_vibration_holding_levers_steering_wheels',
				text: 'Holding levers, steering wheels, etc. that transmit vibrations?',
				required: true
			}
		],
		conditional_questions: [
			{
				key: 'whole_body_vibration_exceeds_table_1_annex_v_resolution_295_03',
				text: 'Does the vibration value exceed the limits established in Table I, in the section corresponding to Hand-Arm Vibration, Annex V, Resolution MTEySS N° 295/03?',
				required: false,
				depends_on: [
					'whole_body_vibration_tools_produce_vibrations',
					'whole_body_vibration_holding_pieces_while_machining',
					'whole_body_vibration_holding_levers_steering_wheels'
				],
				conditional_required: true
			},
			{
				key: 'whole_body_vibration_early_signs_diseases_article_1',
				text: 'Does the worker show any early signs of the diseases mentioned in Article 1 of this Resolution?',
				required: false,
				depends_on: [
					'whole_body_vibration_tools_produce_vibrations',
					'whole_body_vibration_holding_pieces_while_machining',
					'whole_body_vibration_holding_levers_steering_wheels'
				],
				conditional_required: true
			}
		]
	},
	{
		key: 'thermal_comfort',
		name: I18n.get('Thermal comfort'),
		mandatory_questions: [
			{
				key: 'thermal_comfort_uncomfortable_temperatures_workplace',
				text: 'Are uncomfortable temperatures perceived in the workplace for performing tasks?',
				required: true
			}
		],
		conditional_questions: [
			{
				key: 'thermal_comfort_fanger_curve_outside_comfort_zone',
				text: 'Is the result of using the Fanger Comfort Curve outside the comfort zone?',
				required: false,
				depends_on: ['thermal_comfort_uncomfortable_temperatures_workplace'],
				conditional_required: true
			}
		]
	},
	{
		key: 'contact_stress',
		name: I18n.get('Contact stress'),
		mandatory_questions: [
			{
				key: 'contact_stress_body_pressure_tool_surface_machine',
				text: 'Is any part of the body resting under pressure against a tool, work surface, machine tool, or parts and materials?',
				required: true
			}
		],
		conditional_questions: [
			{
				key: 'contact_stress_rest_wrist_forearm_armpit_thigh_other_body_segment',
				text: 'Does the worker rest their wrist, forearm, armpit, thigh, or another body segment on a sharp or edged surface?',
				required: false,
				depends_on: ['contact_stress_body_pressure_tool_surface_machine'],
				conditional_required: true
			},
			{
				key: 'contact_stress_hand_tools_press_fingers_dominant_hand_palm',
				text: 'Does the worker use hand tools or manipulate parts that press on their fingers and/or dominant hand palm?',
				required: false,
				depends_on: ['contact_stress_body_pressure_tool_surface_machine'],
				conditional_required: true
			},
			{
				key: 'contact_stress_percussion_movements_parts_tools',
				text: 'Does the worker perform percussion movements on parts or tools?',
				required: false,
				depends_on: ['contact_stress_body_pressure_tool_surface_machine'],
				conditional_required: true
			},
			{
				key: 'contact_stress_early_signs_diseases_article_1',
				text: 'Does the worker show any early signs of the diseases mentioned in Article 1 of this Resolution?',
				required: false,
				depends_on: ['contact_stress_body_pressure_tool_surface_machine'],
				conditional_required: true
			}
		]
	}
];
